import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const DistributionContactsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [term, setTerm] = useState("");
  const [portalFilter, setPortalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, data: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DistributionNetworkApi.getContacts({
        companyId: companyId || undefined,
        includeInactive: true,
      });
      if (!res.ok) {
        toast.error(res.message || "خطا در دریافت مخاطبین");
        setRows([]);
        return;
      }
      setRows(
        (res.data || []).map((r) => ({
          ...r,
          portalAccessTitle: r.hasPortalAccess ? r.portalRoleTitle : "—",
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    DistributionNetworkApi.getCompanyLookups().then((r) =>
      setCompanies(r.data || [])
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byPortal =
    portalFilter === "with"
      ? rows.filter((r) => r.hasPortalAccess)
      : portalFilter === "without"
        ? rows.filter((r) => !r.hasPortalAccess)
        : rows;
  const filtered = !term
    ? byPortal
    : byPortal.filter((r) =>
        `${r.fullName || ""} ${r.mobile || ""} ${r.position || ""} ${r.companyName || ""} ${r.areaName || ""}`
          .toLowerCase()
          .includes(term.toLowerCase())
      );

  const headCells = [
    { id: "updateOn", label: "بروزرسانی", type: "date" },
    { id: "companyName", label: "شرکت توزیع" },
    { id: "areaName", label: "امور برق" },
    { id: "fullName", label: "نام" },
    { id: "position", label: "سمت" },
    { id: "contactTypeTitle", label: "نوع" },
    { id: "mobile", label: "موبایل" },
    { id: "statusTitle", label: "وضعیت" },
    { id: "portalAccessTitle", label: "دسترسی پورتال" },
  ];

  const confirmedDelete = async () => {
    const res = await DistributionNetworkApi.deleteContact(deleteConfirm.data.id);
    return {
      isSuccess: res.ok,
      message: res.message || (res.ok ? "حذف شد" : "خطا در حذف"),
    };
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
        <DeleteConfirm
          message={
            deleteConfirm.data.hasPortalAccess
              ? `"${deleteConfirm.data.fullName || ""}" حساب پورتال شرکت توزیع دارد؛ با حذف مخاطب، دسترسی او هم حذف می‌شود. ادامه می‌دهید؟`
              : `برای حذف "${deleteConfirm.data.fullName || ""}" اطمینان دارید؟`
          }
          open={deleteConfirm.open}
          onClose={(loadAgain) => {
            setDeleteConfirm({ open: false, data: {} });
            if (loadAgain) load();
          }}
          onSubmit={confirmedDelete}
        />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="مخاطبین شبکه توزیع"
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Link to="/SLN/Management/DistributionContacts/create">
                  <Button variant="contained" startIcon={<AddIcon />}>
                    ایجاد
                  </Button>
                </Link>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                <IconButton color="primary" onClick={load} disabled={loading}>
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="شرکت توزیع"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">همه</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="دسترسی پورتال"
                value={portalFilter}
                onChange={(e) => setPortalFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="with">دارای حساب پورتال</MenuItem>
                <MenuItem value="without">بدون حساب پورتال</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="جستجو"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>
            <EnhancedTable
              rowKey="id"
              rows={filtered}
              headCells={headCells}
              pageSize={100}
              loading={loading}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) =>
                    navigate(`/SLN/Management/DistributionContacts/${row.id}`),
                },
                {
                  icon: <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "حذف",
                  onClick: (row) => setDeleteConfirm({ open: true, data: row }),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DistributionContactsPage;
